import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// OtpVerification archetype — channel verification gate.
//
// Control-archetype (isFormArchetype: false): owns its own advance call.
//
// Config shape:
//   { channels: ('email' | 'sms')[] }   — at least one required.
//
// Runtime behaviour:
//   1. Reads `payload.verified` to discover which channels are already
//      verified (populated by the SPA from server-truth flags).
//   2. For each un-verified required channel, renders a "Send verification"
//      button that calls `adapters.sendVerification`.
//   3. After sending, shows a resend link + a code-entry input so the
//      user can either click the magic link in a new tab OR type/paste
//      the 6-character hex suffix from the URL manually.
//   4. Confirm calls `adapters.confirmVerification` with the raw token
//      (or the code-entry value).
//   5. Once all required channels are verified, calls `advance({})` once.
//
// The magic-link resume flow (user clicks the link in a new tab) is
// handled outside this component by the SPA's App-level URL handler —
// it calls /verify/resume, gets a new bearer, calls /verify/confirm,
// then re-reads the draft (which now has the verified flag set). On the
// next render, payload.verified.<channel> is true and this component
// auto-advances.
import { useEffect, useState, useRef } from 'react';
/** Narrow a config value to a Channel[]. */
function getChannels(config) {
    const raw = config['channels'];
    if (!Array.isArray(raw))
        return ['email'];
    return raw.filter((c) => c === 'email' || c === 'sms');
}
function makeChannelState() {
    return { sent: false, confirmed: false, inputValue: '', error: null, busy: false };
}
export function OtpVerificationComponent(props) {
    const { config, payload, adapters, advance } = props;
    const channels = getChannels(config);
    // Derive initial confirmed state from payload.verified.
    const verifiedInPayload = payload.verified ?? {};
    // channelStates tracks UI state per channel.
    const [states, setStates] = useState(() => {
        const out = { email: makeChannelState(), sms: makeChannelState() };
        // Pre-confirm from payload so a re-render after resume doesn't reset.
        if (verifiedInPayload.email)
            out.email = { ...out.email, confirmed: true, sent: true };
        if (verifiedInPayload.sms)
            out.sms = { ...out.sms, confirmed: true, sent: true };
        return out;
    });
    // Sync payload.verified changes into confirmed state (e.g. after magic-link
    // tab returns and App re-reads the draft).
    useEffect(() => {
        setStates((prev) => {
            const next = { ...prev };
            if (verifiedInPayload.email && !prev.email.confirmed) {
                next.email = { ...prev.email, confirmed: true, sent: true };
            }
            if (verifiedInPayload.sms && !prev.sms.confirmed) {
                next.sms = { ...prev.sms, confirmed: true, sent: true };
            }
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [verifiedInPayload.email, verifiedInPayload.sms]);
    // Track whether we've already fired advance to prevent double-call.
    const hasAdvanced = useRef(false);
    // Auto-advance once all required channels are confirmed.
    useEffect(() => {
        if (hasAdvanced.current)
            return;
        const allDone = channels.every((ch) => states[ch].confirmed);
        if (allDone && channels.length > 0) {
            hasAdvanced.current = true;
            void advance({});
        }
    }, [channels, states, advance]);
    function patch(ch, delta) {
        setStates((prev) => ({ ...prev, [ch]: { ...prev[ch], ...delta } }));
    }
    async function onSend(ch) {
        if (!adapters.sendVerification) {
            patch(ch, { error: 'Verification adapter not wired by the host.' });
            return;
        }
        patch(ch, { busy: true, error: null });
        try {
            await adapters.sendVerification(ch);
            patch(ch, { sent: true, busy: false });
        }
        catch (e) {
            patch(ch, { busy: false, error: e instanceof Error ? e.message : 'Failed to send. Please try again.' });
        }
    }
    async function onConfirm(ch) {
        const { inputValue } = states[ch];
        const token = inputValue.trim();
        if (!token) {
            patch(ch, { error: 'Please enter the verification code from the link.' });
            return;
        }
        if (!adapters.confirmVerification) {
            patch(ch, { error: 'Confirmation adapter not wired by the host.' });
            return;
        }
        patch(ch, { busy: true, error: null });
        try {
            await adapters.confirmVerification(ch, token);
            patch(ch, { confirmed: true, busy: false });
        }
        catch (e) {
            patch(ch, { busy: false, error: e instanceof Error ? e.message : 'Verification failed. Please try again.' });
        }
    }
    const channelLabel = {
        email: 'email address',
        sms: 'phone number',
    };
    return (_jsxs("div", { className: "flex flex-col gap-6", children: [_jsxs("div", { children: [_jsx("h3", { className: "m-0 text-lg font-semibold", children: "Verify your contact info" }), _jsxs("p", { className: "m-0 mt-1 text-sm text-[var(--bcbb-text-muted)]", children: ["We'll send a verification link to confirm your", ' ', channels.map((c) => channelLabel[c]).join(' and '), "."] })] }), channels.map((ch) => {
                const st = states[ch];
                if (st.confirmed) {
                    return (_jsxs("div", { className: "flex items-center gap-2 rounded-md bg-[var(--bcbb-success-bg,#d1fae5)] px-4 py-3 text-sm font-medium text-[var(--bcbb-success,#065f46)]", children: [_jsx("span", { "aria-hidden": true, children: "\u2713" }), _jsxs("span", { children: [ch === 'email' ? 'Email' : 'Phone', " verified"] })] }, ch));
                }
                return (_jsxs("div", { className: "flex flex-col gap-3 rounded-md border p-4", style: { borderColor: 'var(--bcbb-border)' }, children: [_jsxs("p", { className: "m-0 text-sm font-medium", children: [ch === 'email' ? 'Email' : 'Phone', " verification"] }), !st.sent ? (_jsx("button", { type: "button", onClick: () => void onSend(ch), disabled: st.busy, className: "self-start rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60", style: { background: 'var(--bcbb-primary)' }, children: st.busy ? 'Sending…' : `Send verification ${ch === 'email' ? 'email' : 'text'}` })) : (_jsxs(_Fragment, { children: [_jsxs("p", { className: "m-0 text-sm text-[var(--bcbb-text-muted)]", children: ["We sent a verification link to your ", channelLabel[ch], ". Click the link in that message, or paste the code from the URL into the field below."] }), _jsxs("div", { className: "flex gap-2", children: [_jsx("input", { type: "text", placeholder: "Paste code or token", value: st.inputValue, onChange: (e) => patch(ch, { inputValue: e.target.value, error: null }), onKeyDown: (e) => { if (e.key === 'Enter') {
                                                e.preventDefault();
                                                void onConfirm(ch);
                                            } }, className: "flex-1 rounded-md border px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)' }, autoComplete: "one-time-code", "aria-label": `Verification code for ${channelLabel[ch]}` }), _jsx("button", { type: "button", onClick: () => void onConfirm(ch), disabled: st.busy || !st.inputValue.trim(), className: "rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-60", style: { background: 'var(--bcbb-primary)' }, children: st.busy ? 'Verifying…' : 'Verify' })] }), _jsx("button", { type: "button", onClick: () => void onSend(ch), disabled: st.busy, className: "self-start text-sm underline disabled:opacity-60", style: { color: 'var(--bcbb-primary)' }, children: "Resend" })] })), st.error && (_jsx("p", { className: "m-0 text-sm text-[var(--bcbb-danger,#dc2626)]", children: st.error }))] }, ch));
            })] }));
}
export const OtpVerificationArchetype = {
    name: 'OtpVerification',
    label: 'OTP / Email+SMS Verification',
    description: 'Verification gate: sends magic-link tokens via email and/or SMS; ' +
        'advances automatically once all configured channels are verified.',
    component: OtpVerificationComponent,
    isFormArchetype: false,
    fields: {
        channels: {
            kind: 'select',
            label: 'Channels',
            options: [
                { label: 'Email only', value: ['email'] },
                { label: 'SMS only', value: ['sms'] },
                { label: 'Email + SMS', value: ['email', 'sms'] },
            ],
            defaultValue: ['email'],
            hint: 'Which channels must the user verify before advancing.',
        },
    },
    defaultConfig: {
        channels: ['email'],
    },
};
